import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Apollo } from 'apollo-angular';
import { gql } from 'apollo-angular';

@Component({
  selector: 'app-sap-query',
  templateUrl: './sap-query.component.html',
  styleUrls: ['./sap-query.component.scss'],
  standalone: false,
})
export class SapQueryComponent implements OnInit {
  queryForm: FormGroup;
  loading = false;
  result: any = null;
  error: string | null = null;
  executionTime: number | null = null;
  showRequestBody = false;

  httpMethods = [
    { value: 'GET', label: 'GET' },
    { value: 'POST', label: 'POST' },
    { value: 'PATCH', label: 'PATCH' },
    { value: 'PUT', label: 'PUT' },
    { value: 'DELETE', label: 'DELETE' },
  ];

  constructor(
    private fb: FormBuilder,
    private apollo: Apollo
  ) {
    this.queryForm = this.fb.group({
      method: ['GET', [Validators.required]],
      query: ['', [Validators.required, Validators.minLength(1)]],
      requestBody: ['{}'],
      description: [''],
    });
  }

  ngOnInit(): void {
    // Initialize with a sample query
    this.queryForm.patchValue({
      method: 'GET',
      query: 'Items?$select=ItemCode,ItemName,BarCode&$top=10',
      description: 'Sample query to fetch first 10 items',
    });

    // Watch for method changes to show/hide request body field
    this.queryForm.get('method')?.valueChanges.subscribe(method => {
      this.showRequestBody = ['POST', 'PATCH', 'PUT'].includes(method);
      if (
        this.showRequestBody &&
        this.queryForm.get('requestBody')?.value === '{}'
      ) {
        // Provide a sample body for PATCH operations
        if (method === 'PATCH') {
          this.queryForm.patchValue({
            requestBody: JSON.stringify(
              {
                ItemName: 'Updated Item Name',
              },
              null,
              2
            ),
          });
        }
      }
    });
  }

  onSubmit(): void {
    if (this.queryForm.valid) {
      this.executeQuery();
    }
  }

  executeQuery(): void {
    this.loading = true;
    this.error = null;
    this.result = null;
    this.executionTime = null;

    const startTime = window.performance.now();
    const method = this.queryForm.get('method')?.value;
    const query = this.queryForm.get('query')?.value;
    const requestBody = this.queryForm.get('requestBody')?.value;

    // Parse the request body if it's provided
    let bodyData = null;
    if (this.showRequestBody && requestBody) {
      try {
        bodyData = JSON.parse(requestBody);
      } catch {
        this.error = 'Invalid JSON in request body';
        this.loading = false;
        return;
      }
    }

    const EXECUTE_SAP_QUERY = gql`
      mutation ExecuteSapQuery(
        $query: String!
        $method: String!
        $body: String
      ) {
        executeSapQuery(query: $query, method: $method, body: $body) {
          success
          data
          error
        }
      }
    `;

    this.apollo
      .mutate({
        mutation: EXECUTE_SAP_QUERY,
        variables: {
          query: query,
          method: method,
          body: bodyData ? JSON.stringify(bodyData) : null,
        },
      })
      .subscribe({
        next: (response: any) => {
          const endTime = window.performance.now();
          this.executionTime = endTime - startTime;
          this.loading = false;
          if (response.data?.executeSapQuery?.success) {
            this.result = response.data.executeSapQuery.data;
          } else {
            this.error =
              response.data?.executeSapQuery?.error || 'Unknown error occurred';
          }
        },
        error: error => {
          const endTime = window.performance.now();
          this.executionTime = endTime - startTime;
          this.loading = false;
          this.error = error.message || 'Failed to execute query';
        },
      });
  }

  clearResult(): void {
    this.result = null;
    this.error = null;
    this.executionTime = null;
  }

  formatJSON(data: any): string {
    return JSON.stringify(data, null, 2);
  }
}
